import React, { useEffect, useState, Fragment } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, EditIcon, TrashIcon, EyeIcon, ChevronDownIcon, ArrowUpIcon, ArrowDownIcon, ChevronRightIcon, PackageIcon, LayersIcon } from 'lucide-react';
import { BulkPriceEditModal } from '../../components/dealer/BulkPriceEditModal';
export const InventoryManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isLoading, setIsLoading] = useState(true);
  const [isPriceEditModalOpen, setIsPriceEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState<string[]>([]);
  // Mock data for inventory
  const inventory = [{
    id: 'iphone-12',
    name: 'iPhone 12',
    category: 'Smartphones',
    brand: 'Apple',
    basePrice: 699,
    totalStock: 45,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
    variants: [{
      id: 'iphone-12-128gb-black',
      model: 'iPhone 12',
      storage: '128GB',
      color: 'Black',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
      prices: {
        new: 1088,
        excellence: 988,
        good: 888,
        fair: 788
      },
      quantities: {
        new: 1,
        excellence: 5,
        good: 10,
        fair: 3
      }
    }, {
      id: 'iphone-12-256gb-black',
      model: 'iPhone 12',
      storage: '256GB',
      color: 'Black',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
      prices: {
        new: 1088,
        excellence: 988,
        good: 888,
        fair: 788
      },
      quantities: {
        new: 1,
        excellence: 2,
        good: 7,
        fair: 5
      }
    }, {
      id: 'iphone-12-512gb-black',
      model: 'iPhone 12',
      storage: '512GB',
      color: 'Black',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a1zZe5YfTvqp2TmKQmQWqk/image.png',
      prices: {
        new: 1088,
        excellence: 988,
        good: 888,
        fair: 788
      },
      quantities: {
        new: 1,
        excellence: 0,
        good: 2,
        fair: 8
      }
    }]
  }, {
    id: 'samsung-s24',
    name: 'Samsung Galaxy S24',
    category: 'Smartphones',
    brand: 'Samsung',
    basePrice: 799,
    totalStock: 37,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9CLQER3mW4v2Jv4RPGHc1W/image.png',
    variants: [{
      id: 'samsung-s24-128gb-black',
      model: 'Samsung Galaxy S24',
      storage: '128GB',
      color: 'Black',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9CLQER3mW4v2Jv4RPGHc1W/image.png',
      prices: {
        new: 999,
        excellence: 899,
        good: 799,
        fair: 699
      },
      quantities: {
        new: 3,
        excellence: 7,
        good: 12,
        fair: 5
      }
    }, {
      id: 'samsung-s24-256gb-black',
      model: 'Samsung Galaxy S24',
      storage: '256GB',
      color: 'Black',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9CLQER3mW4v2Jv4RPGHc1W/image.png',
      prices: {
        new: 1099,
        excellence: 999,
        good: 899,
        fair: 799
      },
      quantities: {
        new: 2,
        excellence: 3,
        good: 4,
        fair: 1
      }
    }]
  }, {
    id: 'macbook-air-m2',
    name: 'MacBook Air M2',
    category: 'Laptops',
    brand: 'Apple',
    basePrice: 999,
    totalStock: 18,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qcMnDWqru55oVRbBxJg3SM/image.png',
    variants: [{
      id: 'macbook-air-m2-256gb-gray',
      model: 'MacBook Air M2',
      storage: '256GB',
      color: 'Space Gray',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qcMnDWqru55oVRbBxJg3SM/image.png',
      prices: {
        new: 1299,
        excellence: 1099,
        good: 999,
        fair: 899
      },
      quantities: {
        new: 2,
        excellence: 5,
        good: 7,
        fair: 4
      }
    }]
  }];
  // Categories for filter
  const categories = ['All', 'Smartphones', 'Laptops', 'Tablets', 'Accessories'];
  // Simulate loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  // Filter inventory based on search term and category
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy === 'price') {
      comparison = a.basePrice - b.basePrice;
    } else if (sortBy === 'stock') {
      comparison = a.totalStock - b.totalStock;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });
  const handleSort = field => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  const openPriceEditModal = product => {
    setSelectedProduct(product);
    setIsPriceEditModalOpen(true);
  };
  const handleSavePrices = updatedVariants => {
    console.log('Saving updated variants:', updatedVariants);
    // In a real app, this would make an API call to update the prices
  };
  const renderSortIcon = field => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" data-id="element-2497" /> : <ArrowDownIcon className="h-4 w-4 ml-1" data-id="element-2498" />;
  };
  const toggleProductExpansion = productId => {
    setExpandedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };
  // Find the parent product for a given SKU
  const findParentProduct = skuId => {
    for (const product of inventory) {
      const matchingVariant = product.variants.find(variant => variant.id === skuId);
      if (matchingVariant) {
        return {
          ...product,
          variants: [matchingVariant] // Return only the matching variant
        };
      }
    }
    return null;
  };
  // Open price edit modal for a specific SKU
  const openSkuPriceEditModal = skuId => {
    const parentProduct = findParentProduct(skuId);
    if (parentProduct) {
      setSelectedProduct(parentProduct);
      setIsPriceEditModalOpen(true);
    }
  };
  return <div className="container mx-auto" data-id="element-2499">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-2500">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-2501">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0" data-id="element-2502">
            Inventory Management
          </h1>
          <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2503">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-2504" />
            Add New Product
          </button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center mb-6 space-y-4 md:space-y-0 md:space-x-4" data-id="element-2505">
          <div className="relative flex-grow" data-id="element-2506">
            <input type="text" placeholder="Search products..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2507" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-2508" />
          </div>
          <div className="relative" data-id="element-2509">
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" data-id="element-2510">
              {categories.map(category => <option key={category} value={category} data-id="element-2511">
                  {category}
                </option>)}
            </select>
            <ChevronDownIcon className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" data-id="element-2512" />
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2513">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-2514" />
            More Filters
          </button>
        </div>
        {isLoading ? <div className="flex justify-center items-center h-64" data-id="element-2515">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" data-id="element-2516"></div>
          </div> : <div className="overflow-x-auto" data-id="element-2517">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2518">
              <thead className="bg-gray-50" data-id="element-2519">
                <tr data-id="element-2520">
                  <th className="w-10 px-3 py-3" data-id="element-2521"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2522">
                    Products
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2523">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2524">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2525">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2526">
                    SKU
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2527">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2528">
                {filteredInventory.map(item => <Fragment key={item.id} data-id="element-2529">
                    {/* SPU Row */}
                    <tr className={`hover:bg-gray-50 ${expandedProducts.includes(item.id) ? 'bg-blue-50' : 'bg-gray-50'}`} data-id="element-2530">
                      <td className="px-3 py-4 whitespace-nowrap text-center" data-id="element-2531">
                        <button onClick={() => toggleProductExpansion(item.id)} className="text-gray-500 hover:text-gray-700 focus:outline-none" data-id="element-2532">
                          {expandedProducts.includes(item.id) ? <ChevronDownIcon className="h-5 w-5" data-id="element-2533" /> : <ChevronRightIcon className="h-5 w-5" data-id="element-2534" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2535">
                        <div className="flex items-center" data-id="element-2536">
                          <div className="flex-shrink-0 h-10 w-10" data-id="element-2537">
                            <img src={item.image} alt={item.name} className="h-10 w-10 object-contain" data-id="element-2538" />
                          </div>
                          <div className="ml-4" data-id="element-2539">
                            <div className="text-sm font-medium text-gray-900" data-id="element-2540">
                              {item.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2541">
                              {item.brand}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2542">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-2543">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2544">
                        ${item.basePrice}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2545">
                        {item.totalStock} units
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2546">
                        {item.variants.length} variants
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2547">
                        <button onClick={() => openPriceEditModal(item)} className="text-blue-600 hover:text-blue-900 mr-3" title="Edit all variant prices" data-id="element-2548">
                          <EditIcon className="h-5 w-5" data-id="element-2549" />
                        </button>
                        <button className="text-gray-600 hover:text-gray-900 mr-3" data-id="element-2550">
                          <EyeIcon className="h-5 w-5" data-id="element-2551" />
                        </button>
                        <button className="text-red-600 hover:text-red-900" data-id="element-2552">
                          <TrashIcon className="h-5 w-5" data-id="element-2553" />
                        </button>
                      </td>
                    </tr>
                    {/* SKU Rows (only shown when expanded) */}
                    {expandedProducts.includes(item.id) && item.variants.map(variant => <tr key={variant.id} className="bg-white hover:bg-gray-50 border-t border-gray-100" data-id="element-2554">
                          <td className="px-3 py-4" data-id="element-2555"></td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2556">
                            <div className="flex items-center pl-4 ml-4 border-l-2 border-gray-300" data-id="element-2557">
                              <div className="flex-shrink-0 h-4 w-4 mr-3" data-id="element-2558">
                                <span className="inline-block h-4 w-4 rounded-full" style={{
                        backgroundColor: variant.color.toLowerCase()
                      }} data-id="element-2559"></span>
                              </div>
                              <div data-id="element-2560">
                                <div className="text-sm font-medium text-gray-900" data-id="element-2561">
                                  {variant.storage} {variant.color}
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-2562">
                                  SKU: {variant.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2563">
                            {/* Intentionally empty for alignment */}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2564">
                            <div className="space-y-1 text-sm" data-id="element-2565">
                              <div className="grid grid-cols-2 gap-x-2" data-id="element-2566">
                                <span className="text-green-600 font-medium" data-id="element-2567">
                                  New:
                                </span>
                                <span className="text-green-600 font-medium text-right" data-id="element-2568">
                                  ${variant.prices.new}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2" data-id="element-2569">
                                <span className="text-blue-600 font-medium" data-id="element-2570">
                                  Excellence:
                                </span>
                                <span className="text-blue-600 font-medium text-right" data-id="element-2571">
                                  ${variant.prices.excellence}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2" data-id="element-2572">
                                <span className="text-amber-600 font-medium" data-id="element-2573">
                                  Good:
                                </span>
                                <span className="text-amber-600 font-medium text-right" data-id="element-2574">
                                  ${variant.prices.good}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2" data-id="element-2575">
                                <span className="text-gray-600 font-medium" data-id="element-2576">
                                  Fair:
                                </span>
                                <span className="text-gray-600 font-medium text-right" data-id="element-2577">
                                  ${variant.prices.fair}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2578">
                            <div className="space-y-1 text-sm" data-id="element-2579">
                              <div className="grid grid-cols-1" data-id="element-2580">
                                <span className="text-right" data-id="element-2581">
                                  {variant.quantities.new} units
                                </span>
                              </div>
                              <div className="grid grid-cols-1" data-id="element-2582">
                                <span className="text-right" data-id="element-2583">
                                  {variant.quantities.excellence} units
                                </span>
                              </div>
                              <div className="grid grid-cols-1" data-id="element-2584">
                                <span className="text-right" data-id="element-2585">
                                  {variant.quantities.good} units
                                </span>
                              </div>
                              <div className="grid grid-cols-1" data-id="element-2586">
                                <span className="text-right" data-id="element-2587">
                                  {variant.quantities.fair} units
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2588">
                            {variant.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2589">
                            <button onClick={() => openSkuPriceEditModal(variant.id)} className="text-blue-600 hover:text-blue-900 mr-3" title="Edit this SKU's prices" data-id="element-2590">
                              <EditIcon className="h-5 w-5" data-id="element-2591" />
                            </button>
                          </td>
                        </tr>)}
                  </Fragment>)}
              </tbody>
            </table>
          </div>}
      </div>
      <BulkPriceEditModal isOpen={isPriceEditModalOpen} onClose={() => setIsPriceEditModalOpen(false)} product={selectedProduct} onSave={handleSavePrices} data-id="element-2592" />
    </div>;
};